import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import * as proveAuth from '@prove-identity/prove-auth';
import { OtpStartInput } from '@prove-identity/prove-auth';
import PhoneNumberInput from '@prove-identity/prove-auth/build/lib/proveauth/internal/phone-number-input';

import { Button } from '@components/shared/Button/Button';
import { TextInput } from '@components/shared/form/TextInput';
import { useFormContext } from '@context/FormContext';
import { useProve } from '@context/proveProvider';
import { Screens } from '@shared/constants/screens';
import useForm, { validate } from '@shared/hooks/useForm';
import { CDX_ACTIONS, IVerifyMobileNumberForm } from 'types/form';
import { useTexts } from 'utils/useTexts';
import ProveClientSdk from '@services/proveClientSdk';
import { validate as validateProve } from '@services/prove';
import { convertKeysToSnakeCase, toDefaultPhoneFormat } from 'utils/stringFormat';
import { createErrorResponse } from '@services/utils';
import useEffectOnce from '@shared/hooks/useEffectOnce';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { PhoneTextInput } from '@components/shared/form/PhoneTextInput';
import OverlaySpinner from '@components/shared/OverlaySpinner/OverlaySpinner';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';

const BLANK_OTP = '';

export interface IEnterPhoneNumberForm {
  phone: string;
}

const VerifyMobileNumberForm = () => {
  const { currentStep, handleStep, verifyYourIdentity, updateVerifyYourIdentity, dispatch, submitStart, submitSuccess } = useFormContext();
  const { t } = useTexts();
  const { next } = useScreenNavigator();

  const { trackPage, startInteraction, trackFormSubmit, trackClick } = useAnalytics({
    ...SEGMENT_SCREEN_PARAMS.PROVE_IDENTITY,
    verification_method: 'sms_code',
    field_count: 2,
    required_field_count: 2,
  });

  const { authToken, correlationId, setChallengeMissing, setNextStep, setAuthToken } = useProve();
  const authRef = useRef<proveAuth.CancelablePromise<void> | null>(null);
  const cancelledRef = useRef<boolean>(false);
  
  const [otp, setOtp] = useState<string>(BLANK_OTP);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [displayPhoneInput, setDisplayPhoneInput] = useState<boolean | undefined>(undefined);
  const [otpActions, setOtpActions] = useState<{
    submitOtp: ((otp: string) => void) | null;
    cancelOtpFinish: (() => void) | null;
  }>({
    submitOtp: null,
    cancelOtpFinish: null,
  });
  const [phoneAction, setPhoneAction] = useState<{
    submitPhoneNumber: ((phone: string) => void) | null;
  }>({
    submitPhoneNumber: null,
  });



  const verificationCodeValidation = {
    verificationCode: validate
      .string('verificationCode')
      .required(t('verifyMobileNumber.validation.verificationCode'))
      .matches(/^\d{4}$/, t('verifyMobileNumber.validation.validVerificationCode')),
  };

  const phoneNumberValidation = {
    phone: validate
      .string('phone')
      .required(t('verifyMobileNumber.validation.phone.required'))
      .matches(/^\d{10}$/, t('verifyMobileNumber.validation.phone.validPhoneNumber')),
  };

  const validationSchema = displayPhoneInput ? phoneNumberValidation : verificationCodeValidation;

  const { errors, handleChange, handleSubmit } = useForm<IVerifyMobileNumberForm | IEnterPhoneNumberForm>({
    initialValues: {
      verificationCode: '',
    },
    validationSchema,
    onSubmit: async () => {
      if (displayPhoneInput) {
        handlePhoneSubmit(phoneNumber);
        setDisplayPhoneInput(false);
      } else {
        handleVerifyOTP();
      }
    },
  });

  const otpInputError: Partial<Record<keyof IVerifyMobileNumberForm, string>> = !displayPhoneInput
    ? (errors as Record<keyof IVerifyMobileNumberForm, string>)
    : {};
  const phoneInputError: Partial<Record<keyof IEnterPhoneNumberForm, string>> = displayPhoneInput
    ? (errors as Record<keyof IEnterPhoneNumberForm, string>)
    : {};

  useEffect(() => {
    next(currentStep, 'onLoad', { authToken });
    resetFields();
    sendOTP();

    return () => {
      setAuthToken('');
      handleCancelOtp();
      if (authRef.current) {
        authRef.current.cancel();
      }
    };
  }, []);

  useEffectOnce(() =>
    trackPage(t('pageTitles.VerifyYourMobileNumber'), {
      field_count: 1,
      required_field_count: 1,
    })
  );

  const resetFields = () => {
    setOtp(BLANK_OTP);
    clearOtpActions();
  };

  const sendOTP = async () => {
    cancelledRef.current = false;
    submitStart();
    try {
      const proveClient = new ProveClientSdk();
      const builder = proveClient.handleOtp(otpStartStep, otpFinishStep, authFinishStep);
      authRef.current = builder.authenticate(authToken as string);
      await authRef.current;
      submitSuccess();
    } catch (error) {
      trackFormSubmit('Form Submitted', {
        submission_fail_reason: convertKeysToSnakeCase(error instanceof Error ? error.message : 'Error sending OTP'),
        submission_status: false,
        verification_method: 'sms_code',
        forward_screen_name: 'contact_info',
      });
      dispatch({
        type: CDX_ACTIONS.SUBMIT_API_ERROR,
        payload: createErrorResponse(
          error instanceof Error ? error.name : 400,
          error instanceof Error ? error.message : 'Error sending OTP'
        ),
      });
    }
  };

  const handleVerifyOTP = () => {
    const enteredOtp = otp;
    submitStart();
    try {
      handleOtpSubmit(enteredOtp);
      submitSuccess();
      trackFormSubmit('Form Submitted', {
        sub_screen_name: 'verify_mobile_code',
        submission_status: true,
        verification_method: 'sms_code',
        forward_screen_name: 'contact_info',
      });
    } catch (error) {
      trackFormSubmit('Form Submitted', {
        submission_fail_reason: convertKeysToSnakeCase(error instanceof Error ? error.message : 'Error submitting OTP'),
        submission_status: false,
        verification_method: 'sms_code',
        forward_screen_name: 'contact_info',
      });
      dispatch({
        type: CDX_ACTIONS.SUBMIT_API_ERROR,
        payload: createErrorResponse(
          error instanceof Error ? error.name : 400,
          error instanceof Error ? error.message : 'Error submitting OTP'
        ),
      });
    }
  };

  const otpStartStep: proveAuth.OtpStartStepFn = async (
    phoneNumberNeeded: boolean,
    phoneValidationError?: proveAuth.PhoneValidationError
  ): Promise<PhoneNumberInput | null> => {
    return new Promise((resolve, reject) => {
      try {
        if (phoneNumberNeeded) {
          setDisplayPhoneInput(true);
          if (phoneValidationError) {
            setDisplayPhoneInput(true);
          }

          const submitPhoneNumber = (phoneNumber: string) => {
            const input: OtpStartInput = {
              phoneNumber: phoneNumber ?? '',
            };
            resolve(input);
          };

          setPhoneAction({ submitPhoneNumber });
        } else {
          setDisplayPhoneInput(false);
          resolve(null);
        }
      } catch (error) {
        dispatch({
          type: CDX_ACTIONS.SUBMIT_API_ERROR,
          payload: createErrorResponse(
            error instanceof Error ? error.name : 400,
            error instanceof Error ? error.message : 'Error sending OTP'
          ),
        });
        reject(error);
      }
    });
  };

  const otpFinishStep: proveAuth.OtpFinishStepFn = async (
    otpError: proveAuth.OtpError | undefined
  ): Promise<proveAuth.OtpFinishResult> => {
    //eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        if (otpError) {
          const message = 'OTP is invalid. Try re-entering the OTP';
          trackFormSubmit('Form Submitted', {
            submission_fail_reason: convertKeysToSnakeCase('OTP is invalid'),
            submission_status: false,
            verification_method: 'sms_code',
            forward_screen_name: 'contact_info',
          });
          dispatch({
            type: CDX_ACTIONS.SUBMIT_API_ERROR,
            payload: createErrorResponse(otpError?.code || 400, message),
          });
        }
        const result = await otpFinishStepHandler();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    });
  };

  const otpFinishStepHandler = (): Promise<proveAuth.OtpFinishResult> => {
    return new Promise<proveAuth.OtpFinishResult>((resolve, reject) => {
      const submitOtp = (otp: string) => {
        const input: proveAuth.OtpFinishInput = {
          otp: otp ?? '',
        };
        const result: proveAuth.OtpFinishResult = {
          input: input,
          resultType: proveAuth.OtpFinishResultType.OnSuccess,
        };
        resolve(result);
      };

      const cancelOtpFinish = () => {
        trackFormSubmit('Form Submitted', {
          submission_fail_reason: 'verification_declined',
          submission_status: false,
          verification_method: 'sms_code',
          forward_screen_name: 'contact_info',
        });
        reject(new Error('User has canceled OTP flow'));
      };

      setOtpActions({ submitOtp, cancelOtpFinish });
    });
  };

  const authFinishStep: proveAuth.AuthFinishStepFn = async (): Promise<void> => {
    if (cancelledRef.current) return;
    const validateResponse = await validateProve({
      correlationId: correlationId as string,
    });
    if ('error' in validateResponse) {
      dispatch({
        type: CDX_ACTIONS.SUBMIT_API_ERROR,
        payload: validateResponse,
      });
    } else {
      if (validateResponse.success) {
        const { next, challengeMissing, phoneNumber } = validateResponse;
        setNextStep(next);
        setChallengeMissing(challengeMissing);
        if (phoneNumber) updateVerifyYourIdentity({ phone: phoneNumber });
        trackFormSubmit('Identity Verification Completed', {
          verification_status: true,
          verification_provider: 'prove',
          verification_method: 'sms_code',
        });
      }
      next(currentStep, 'postSubmitOutcome', { validateResponse });
    }
  };
  const handleOtpSubmit = (otp: string) => {
    if (otpActions.submitOtp) {
      try {
        const result = otpActions.submitOtp(otp);
        return result;
      } catch (error) {
        new Error('OTP Finish Failed');
      }
    }
  };

  const handlePhoneSubmit = (phone: string) => {
    if (phoneAction.submitPhoneNumber) {
      try {
        const result = phoneAction.submitPhoneNumber(phone);
        return result;
      } catch (error) {
        new Error('Phone number entry failed');
      }
    }
  };

  const handleCancelOtp = () => {
    if (otpActions.cancelOtpFinish) {
      otpActions.cancelOtpFinish();
    }
  };

  const clearOtpActions = () => {
    setOtpActions({ submitOtp: null, cancelOtpFinish: null });
  };

  const handleInputChange = <K extends keyof (IVerifyMobileNumberForm & IEnterPhoneNumberForm)>(
    field: K,
    value: (IVerifyMobileNumberForm & IEnterPhoneNumberForm)[K]
  ) => {
    //  eslint-disable-next-line @typescript-eslint/no-explicit-any
    (handleChange as any)(field, value);
  };

  const handleOtpChange = <K extends keyof IVerifyMobileNumberForm>(
    field: K,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setOtp(value);
    handleInputChange(field, value);
  };

  const handlePhoneNumberChange = (phoneNumber: string | ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(phoneNumber.toString());
    const phoneNumberValue = phoneNumber.toString();
    updateVerifyYourIdentity({ phone: phoneNumberValue });
    handleInputChange('phone', phoneNumberValue);
  };

  const handleDidNotReceiveText = () => {
    cancelledRef.current = true;
    handleCancelOtp();
    if (authRef.current) {
      authRef.current.cancel();
    }
    trackClick('CTA Clicked', {
      sub_screen_name: 'verify_mobile_support',
      cta_id: 'verification_resend',
      cta_text: 'I didn\'t receive a text',
      cta_type: 'link',
      cta_context: 'verification_challenge',
    });
    handleStep(Screens.IDENTITY_FAILED);
  };

  if (displayPhoneInput === undefined || displayPhoneInput === null) return <OverlaySpinner size="lg" />;

  return (
    <div>
      {displayPhoneInput ? (
        <form
          onSubmit={handleSubmit}
          onFocus={startInteraction}
        >
          <h1 className="iLendingCDX-form-title">{t('verifyMobileNumber.phoneNumberInput.title')}</h1>

          <h2 className="iLendingCDX-form-title--small iLendingCDX-mb-8">{t('verifyMobileNumber.phoneNumberInput.subTitle')}</h2>
          <PhoneTextInput
            label={t('verifyMobileNumber.phoneNumberInput.phoneNumber')}
            className={'iLendingCDX-mb-4'}
            name="phone"
            value={phoneNumber}
            errorMessage={phoneInputError.phone}
            onChange={(value) => handlePhoneNumberChange(value)}
          />

          <Button
            type="submit"
            className="iLendingCDX-button--rounded"
            size="lg"
          >
            {t('verifyMobileNumber.phoneNumberInput.continue')}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleSubmit}>
          <h1 className="iLendingCDX-form-title">{t('verifyMobileNumber.title')}</h1>

          <h2 className="iLendingCDX-form-title--small iLendingCDX-mb-8">
            {t('verifyMobileNumber.subTitle')}
            {toDefaultPhoneFormat(verifyYourIdentity.phone)}
          </h2>
          <TextInput
            label={t('verifyMobileNumber.verificationCode')}
            className={'iLendingCDX-mb-6'}
            name="verificationCode"
            errorMessage={otpInputError.verificationCode}
            onChange={(e) => handleOtpChange('verificationCode', e)}
          />

          <Button
            type="submit"
            className="iLendingCDX-button--rounded"
            size="lg"
          >
            {t('verifyMobileNumber.verifyNow')}
          </Button>
          <Button
            variant="ghost"
            size="md"
            type="button"
            className={'iLendingCDX-mb-4'}
            onClick={handleDidNotReceiveText}
          >
            {t('verifyMobileNumber.didNotReceiveText')}
          </Button>
        </form>
      )}
    </div>
  );
};

export default VerifyMobileNumberForm;
